import { FunctionComponent, memo, useCallback, useContext, useMemo, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import sha1 from 'sha1';
import { ConfigContext, UserContext } from '../../../../context';
import { useAnalytics } from '../../../hooks';
import { getCssVar, getDeepLinkUrl } from '../../../utils';
import { Button, ButtonType } from '../../Button';
import { Deeplink, DeeplinkType } from '../../Deeplink';
import { IconLabel } from '../../IconLabel';
import { Modal } from '../../Modal';
import { ShareUrlViaSocial } from '../../ShareUrlViaSocial';

import classes from './Share.module.scss';

interface ShareProps {
  storyId: number;
  cardId: number;
  articleQuoteUrl?: string;
  pdfUrl?: string;
}

export const Share: FunctionComponent<ShareProps> = memo(
  ({ storyId, cardId, articleQuoteUrl, pdfUrl }) => {
    const { t } = useTranslation();

    const { id: userId, channelId } = useContext(UserContext).userInfo.userData;

    const { deeplinkEnabled = true, externalUrlEnabled = true } = {
      ...useContext(ConfigContext).config.elements.sharePost,
    };

    const { logItemShared } = useAnalytics();

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const url = useMemo(() => articleQuoteUrl ?? pdfUrl, [articleQuoteUrl, pdfUrl]);

    const logItemShare = useCallback(() => {
      logItemShared({
        user_Id: sha1(`${userId}`),
        channel_id: channelId,
        story_id: storyId,
        item_id: cardId,
        time_stamp: new Date().toISOString(),
      });
    }, [cardId, channelId, logItemShared, storyId, userId]);

    const deeplinkClick = useCallback(() => {
      setIsOpen(false);
      logItemShare();
    }, [logItemShare]);

    const copySourceClick = useCallback(async () => {
      setIsOpen(false);

      if (!navigator.clipboard || !url) {
        return;
      }

      await navigator.clipboard.writeText(url);

      toast(t(`cardActions.${pdfUrl ? 'pdf' : 'source'}-url-copied`));

      logItemShare();
    }, [logItemShare, pdfUrl, t, url]);

    const title = t('share.title');

    const body = useMemo(() => {
      const shareUrl = getDeepLinkUrl({ type: DeeplinkType.CARD, channelId, storyId, cardId });

      return (
        <div className={classes['share']}>
          <div className={classes['share__title']}>{title}</div>

          <ShareUrlViaSocial
            url={shareUrl}
            subject={t('share.email-subject')}
            body={`${t('share.email-body')}\n`}
          />

          {(deeplinkEnabled || (externalUrlEnabled && url)) && (
            <div className={classes['share__buttons']}>
              {deeplinkEnabled && (
                <Deeplink
                  type={DeeplinkType.CARD}
                  storyId={storyId}
                  cardId={cardId}
                  label={t('cardActions.deeplink')}
                  onClick={deeplinkClick}
                  showAsButton
                />
              )}

              {externalUrlEnabled && url && (
                <Button
                  type={ButtonType.secondary}
                  label={t(`cardActions.copy-${pdfUrl ? 'pdf' : 'source'}-url`)}
                  onClick={copySourceClick}
                  fullWidth
                />
              )}
            </div>
          )}
        </div>
      );
    }, [
      cardId,
      channelId,
      copySourceClick,
      deeplinkClick,
      deeplinkEnabled,
      externalUrlEnabled,
      pdfUrl,
      storyId,
      t,
      title,
      url,
    ]);

    return (
      <>
        <IconLabel
          iconId={'share'}
          iconSize={20}
          ariaLabel={t('cardActions.share')}
          color={getCssVar('--action-pane-icon-color')}
          hoverColor={getCssVar('--action-pane-icon-hover-color')}
          onClick={() => setIsOpen(true)}
          {...(!isMobileOnly && { label: t('cardActions.share') })}
        />

        <Modal
          isOpen={isOpen}
          body={body}
          onClose={() => setIsOpen(false)}
          contentStyle={{ width: '26rem' }}
          ariaLabel={title}
        />
      </>
    );
  }
);
